import PropTypes from 'prop-types';
import React from 'react';
import {connect} from 'react-redux';
import VM from 'scratch-vm';
import JSZip from 'jszip';
import HydroRecords from '../components/hydro-records/records.jsx';
import RecordDetail from '../components/hydro-records/record-detail.jsx';
import {fetchRecordJSON, getRecordDetailUrl, getRecordUrls} from './hydro-records';

const urlQuery = new URLSearchParams(window.location.search);
const query = {
    get: key => {
        const examConfig = window.__EXAM_SCRATCH_CONFIG__;
        if (examConfig && Object.prototype.hasOwnProperty.call(examConfig, key)) return examConfig[key];
        return urlQuery.get(key);
    }
};
const enabled = query.get('hydro') === '1';
const recordUrls = getRecordUrls(query);
const celebrationPieces = Array.from({length: 30}, (_, index) => index);

const getCloudSaveUrl = () => {
    const configuredUrl = query.get('cloudSaveUrl');
    if (configuredUrl) return configuredUrl;
    const returnUrl = query.get('returnUrl');
    if (returnUrl && !query.get('submitUrl') && !query.get('problemDraftUrl')) {
        try {
            return new URL('/creative/save/project', returnUrl).href;
        } catch (error) {
            return '';
        }
    }
    return '';
};

const getResponseError = (data, response, fallback) => {
    if (typeof data?.error === 'string') return data.error;
    if (data?.error?.message) return data.error.message;
    if (typeof data?.message === 'string') return data.message;
    if (!response.ok && response.statusText) return response.statusText;
    return fallback;
};

const blobToArrayBuffer = blob => {
    if (blob.arrayBuffer) return blob.arrayBuffer();
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = () => reject(reader.error);
        reader.readAsArrayBuffer(blob);
    });
};

const normalizeProjectBlob = async blob => {
    const zip = await JSZip.loadAsync(blob);
    const projectFile = zip.file('project.json');
    if (!projectFile) return blob;
    const project = JSON.parse(await projectFile.async('string'));
    if (!Array.isArray(project.targets)) return blob;

    const stages = project.targets.filter(target => target && target.isStage);
    if (stages.length > 1) {
        const stage = stages.find(target => target.layerOrder === 0) || stages[0];
        project.targets = project.targets.filter(target => !target.isStage || target === stage);
    }
    const stage = project.targets.find(target => target && target.isStage);
    if (stage) stage.layerOrder = 0;
    project.targets.filter(target => target && !target.isStage)
        .sort((a, b) => (a.layerOrder || 0) - (b.layerOrder || 0))
        .forEach((target, index) => {
            target.layerOrder = index + 1;
        });
    zip.file('project.json', JSON.stringify(project));
    return zip.generateAsync({type: 'blob'});
};

const HydroBridgeHOC = function (WrappedComponent) {
    class HydroBridge extends React.Component {
        constructor (props) {
            super(props);
            this.state = {
                judgeResult: null,
                operation: null,
                saveStatus: 'idle',
                statusMessage: '',
                projectLoadStatus: 'idle',
                projectLoadProgress: 0,
                showProblemDetails: false,
                showRecords: false
            };
            this.saveTimer = null;
            this.draftChangeTimer = null;
            this.draftSaveChain = Promise.resolve();
            this.draftVersion = 0;
            this.examRedrawTimer = null;
            this.loadedTemplateUrl = '';
            this.cloudProjectId = query.get('projectId') || '';
            this.handleMessage = this.handleMessage.bind(this);
            this.handleSaveStart = this.handleSaveStart.bind(this);
            this.handleSubmitAnswer = this.handleSubmitAnswer.bind(this);
            this.handleShowProblemDetails = this.handleShowProblemDetails.bind(this);
            this.handleHideProblemDetails = this.handleHideProblemDetails.bind(this);
            this.handleProblemDetailsClick = this.handleProblemDetailsClick.bind(this);
            this.handleCloseJudgeResult = this.handleCloseJudgeResult.bind(this);
            this.handleShowRecords = this.handleShowRecords.bind(this);
            this.handleHideRecords = this.handleHideRecords.bind(this);
            this.handleCloseSaveStatus = this.handleCloseSaveStatus.bind(this);
            this.loadTemplate = this.loadTemplate.bind(this);
            this.exportProject = this.exportProject.bind(this);
            this.handleProjectChanged = this.handleProjectChanged.bind(this);
        }
        componentDidMount () {
            if (!enabled) return;
            this.props.vm.on('PROJECT_CHANGED', this.handleProjectChanged);
            if (query.get('examMode') === '1') {
                window.__EXAM_SCRATCH_REDRAW__ = () => this.props.vm.renderer?.draw();
                this.examRedrawTimer = window.setInterval(() => {
                    const canvas = this.props.vm.renderer?.canvas;
                    if (document.visibilityState === 'visible' && canvas?.getClientRects().length) {
                        this.props.vm.renderer.draw();
                    }
                }, 50);
            }
            window.addEventListener('message', this.handleMessage);
            window.addEventListener('hydro:save-cloud-start', this.handleSaveStart);
            window.addEventListener('hydro:submit-answer', this.handleSubmitAnswer);
            window.addEventListener('hydro:show-problem-details', this.handleShowProblemDetails);
            window.addEventListener('hydro:show-records', this.handleShowRecords);
            const hostWindow = window.opener || window.parent;
            hostWindow?.postMessage({type: 'hydro:ready'}, '*');
            const templateUrl = query.get('template');
            if (templateUrl) {
                this.loadTemplate(templateUrl).catch(error => {
                    this.loadedTemplateUrl = '';
                    console.error('[hydro] Scratch project load failed', error);
                    window.alert( // eslint-disable-line no-alert
                        `Scratch 作品加载失败：${error?.message || '文件下载不完整或服务器响应超时'}`
                    );
                });
            }
        }
        componentWillUnmount () {
            if (!enabled) return;
            this.props.vm.removeListener('PROJECT_CHANGED', this.handleProjectChanged);
            delete window.__EXAM_SCRATCH_REDRAW__;
            if (this.examRedrawTimer) window.clearInterval(this.examRedrawTimer);
            window.removeEventListener('message', this.handleMessage);
            window.removeEventListener('hydro:save-cloud-start', this.handleSaveStart);
            window.removeEventListener('hydro:submit-answer', this.handleSubmitAnswer);
            window.removeEventListener('hydro:show-problem-details', this.handleShowProblemDetails);
            window.removeEventListener('hydro:show-records', this.handleShowRecords);
            if (this.saveTimer) window.clearTimeout(this.saveTimer);
            if (this.draftChangeTimer) window.clearTimeout(this.draftChangeTimer);
        }
        handleProjectChanged () {
            if (window.parent !== window) {
                window.parent.postMessage({type: 'hydro:project-changed'}, '*');
            }
            const draftUrl = query.get('draftUrl');
            if (draftUrl && (!query.get('examMode') || window.__EXAM_SCRATCH_CONFIG__?.draftReady)) {
                this.draftVersion += 1;
                if (this.draftChangeTimer) window.clearTimeout(this.draftChangeTimer);
                this.draftChangeTimer = window.setTimeout(() => {
                    this.draftChangeTimer = null;
                    this.saveDraftToUrl(draftUrl).catch(() => {});
                }, 1000);
                window.dispatchEvent(new CustomEvent('hydro:exam-draft-save-state', {
                    detail: {state: 'saving', pid: query.get('pid')}
                }));
            }
        }
        saveDraftToUrl (draftUrl) {
            const attempt = this.draftSaveChain.catch(() => {}).then(() => this.performDraftSave(draftUrl));
            this.draftSaveChain = attempt;
            return attempt;
        }
        async performDraftSave (draftUrl) {
            const version = this.draftVersion;
            if (this.draftChangeTimer) window.clearTimeout(this.draftChangeTimer);
            this.draftChangeTimer = null;
            try {
                const project = await normalizeProjectBlob(await this.props.vm.saveProjectSb3());
                const form = new FormData();
                form.append('type', 'scratch');
                form.append('lang', 'scratch3');
                form.append('file', new File([project], 'draft.sb3', {type: 'application/x.scratch.sb3'}));
                const response = await fetch(draftUrl, {
                    method: 'POST', body: form, credentials: 'include', headers: {Accept: 'application/json'}
                });
                const result = await response.json().catch(() => ({}));
                if (!response.ok || result.error) throw new Error(result.error?.message || 'Scratch 草稿保存失败');
                if (version === this.draftVersion) {
                    window.dispatchEvent(new CustomEvent('hydro:exam-draft-save-state', {
                        detail: {state: 'saved', pid: query.get('pid')}
                    }));
                }
            } catch (error) {
                window.dispatchEvent(new CustomEvent('hydro:exam-draft-save-state', {
                    detail: {state: 'failed', pid: query.get('pid')}
                }));
                console.error('[hydro] Scratch draft autosave failed', error);
                this.draftChangeTimer = window.setTimeout(() => {
                    this.draftChangeTimer = null;
                    this.saveDraftToUrl(draftUrl).catch(() => {});
                }, 3000);
                throw error;
            }
        }
        handleSaveStart (event) {
            this.setState({operation: 'cloud', saveStatus: 'saving', statusMessage: ''});
            if (this.saveTimer) window.clearTimeout(this.saveTimer);
            this.saveTimer = window.setTimeout(() => {
                this.saveTimer = null;
                this.setState({saveStatus: 'error', statusMessage: '保存请求超时，请检查网络后重试'});
            }, 180000);
            // In problem mode Scratch is opened directly from the problem page.
            // That page does not own the creative-workspace save message, so save
            // the exported project to the cloud endpoint here instead.
            if (query.get('problemDraftUrl')) this.saveProblemDraft();
            else if (getCloudSaveUrl() || query.get('submitUrl')) {
                this.saveProjectToCloud(Boolean(event?.detail?.saveAs));
            }
        }
        async uploadProblemDraft (blob) {
            const draftUrl = query.get('problemDraftUrl');
            if (!draftUrl) throw new Error('题目草稿保存地址不可用');
            const form = new FormData();
            form.append('type', 'scratch');
            form.append('lang', 'scratch3');
            form.append('file', new File([blob], 'draft.sb3', {
                type: 'application/x.scratch.sb3'
            }));
            const response = await fetch(draftUrl, {
                body: form,
                credentials: 'include',
                headers: {Accept: 'application/json'},
                method: 'POST'
            });
            const result = await response.json().catch(() => ({}));
            if (!response.ok || result.error) {
                throw new Error(result.error?.message || response.statusText || '题目草稿保存失败');
            }
        }
        async saveProblemDraft () {
            try {
                const blob = await normalizeProjectBlob(await this.props.vm.saveProjectSb3());
                await this.uploadProblemDraft(blob);
                if (this.saveTimer) window.clearTimeout(this.saveTimer);
                this.saveTimer = null;
                this.setState({saveStatus: 'success', statusMessage: '答题程序已保存'});
            } catch (error) {
                if (this.saveTimer) window.clearTimeout(this.saveTimer);
                this.saveTimer = null;
                this.setState({
                    saveStatus: 'error',
                    statusMessage: error?.message || '题目草稿保存失败'
                });
            }
        }
        async saveProjectToCloud (saveAs = false) {
            try {
                const submitUrl = query.get('submitUrl');
                const saveUrl = getCloudSaveUrl() ||
                    new URL('/creative/save/project', submitUrl).href;
                const blob = await normalizeProjectBlob(await this.props.vm.saveProjectSb3());
                const form = new FormData();
                if (saveAs) this.cloudProjectId = '';
                form.append('id', this.cloudProjectId);
                form.append('title', this.props.reduxProjectTitle || query.get('projectTitle') || 'Scratch作品');
                form.append('mode', 'scratch');
                form.append('language', 'python');
                form.append('code', '');
                const cover = await this.captureCover();
                if (cover) form.append('cover', cover);
                form.append('scratch', new File([blob], 'project.sb3', {
                    type: 'application/x.scratch.sb3'
                }));
                const response = await fetch(saveUrl, {
                    body: form,
                    credentials: 'include',
                    headers: {Accept: 'application/json'},
                    method: 'POST'
                });
                const result = await response.json().catch(() => ({}));
                if (!response.ok || result.error) {
                    throw new Error(result.error?.message || response.statusText || 'Save failed');
                }
                this.cloudProjectId = result.id || this.cloudProjectId;
                if (this.saveTimer) window.clearTimeout(this.saveTimer);
                this.saveTimer = null;
                this.setState({saveStatus: 'success', statusMessage: ''});
                if (window.__HYDRO_RETURN_AFTER_SAVE__) {
                    window.__HYDRO_RETURN_AFTER_SAVE__ = false;
                    window.location.assign(query.get('returnUrl') || '/my-projects');
                }
            } catch (error) {
                if (this.saveTimer) window.clearTimeout(this.saveTimer);
                this.saveTimer = null;
                this.setState({
                    saveStatus: 'error',
                    statusMessage: error?.message || '作品上传失败'
                });
            }
        }
        handleCloseSaveStatus () {
            this.setState({operation: null, saveStatus: 'idle', statusMessage: ''});
        }
        handleShowProblemDetails () {
            this.setState({showProblemDetails: true});
        }
        handleHideProblemDetails () {
            this.setState({showProblemDetails: false});
        }
        handleProblemDetailsClick (event) {
            event.stopPropagation();
        }
        handleCloseJudgeResult () {
            this.setState({judgeResult: null});
        }
        handleShowRecords () {
            if (recordUrls) this.setState({showRecords: true});
        }
        handleHideRecords () {
            this.setState({showRecords: false});
        }
        async waitForJudgeResult (rid) {
            const resultTemplate = query.get('resultUrl');
            if (!resultTemplate) throw new Error('Judge result URL is unavailable');
            const resultUrl = resultTemplate.replace('__RID__', rid);
            for (let attempt = 0; attempt < 360; attempt++) {
                const response = await fetch(resultUrl, {
                    credentials: 'include',
                    headers: {Accept: 'application/json'}
                });
                const result = await response.json();
                if (!response.ok || result.error) {
                    throw new Error(getResponseError(result, response, '获取评测结果失败'));
                }
                if (result.done) return result;
                await new Promise(resolve => window.setTimeout(resolve, 250));
            }
            throw new Error('Judge timed out');
        }
        async handleSubmitAnswer () {
            const submitUrl = query.get('submitUrl');
            if (!submitUrl || this.state.saveStatus === 'saving') return;
            this.setState({operation: 'judge', saveStatus: 'saving', statusMessage: ''});
            try {
                const blob = await normalizeProjectBlob(await this.props.vm.saveProjectSb3());
                if (!blob || !blob.size) throw new Error('Scratch 作品生成失败，请稍后重试');
                const form = new FormData();
                form.append('lang', 'scratch3');
                form.append('file', new File([blob], 'main.sb3', {type: 'application/x.scratch.sb3'}));
                const cover = await this.captureCover();
                if (cover) form.append('cover', cover);
                const response = await fetch(submitUrl, {
                    body: form,
                    credentials: 'include',
                    headers: {Accept: 'application/json'},
                    method: 'POST'
                });
                const data = await response.json().catch(() => ({}));
                if (!response.ok || data.error) {
                    throw new Error(getResponseError(data, response, '提交评测失败'));
                }
                const match = data.url && /\/record\/([0-9a-f]{24})/i.exec(data.url);
                const rid = String(data.rid || (match && match[1]) || '');
                if (!rid) throw new Error('Judge record ID is unavailable');
                let judgeResult = await this.waitForJudgeResult(rid);
                if (recordUrls && query.get('examMode') !== '1') {
                    try {
                        judgeResult = await fetchRecordJSON(getRecordDetailUrl(recordUrls, rid));
                    } catch (error) {
                        // The submission succeeded even if the optional detail request failed.
                        judgeResult = {...judgeResult, detailError: '评测已完成，详情暂时加载失败，可在提交记录中刷新查看。'};
                    }
                }
                if (query.get('examMode') === '1') {
                    window.dispatchEvent(new CustomEvent('hydro:exam-answer-submitted', {
                        detail: {pid: query.get('pid'), rid}
                    }));
                }
                this.setState({
                    judgeResult,
                    operation: null,
                    saveStatus: 'idle',
                    statusMessage: ''
                });
            } catch (error) {
                this.setState({
                    saveStatus: 'error',
                    statusMessage: error?.message || '提交或获取评测结果失败'
                });
            }
        }
        async loadTemplate (templateUrl) {
            if (!templateUrl || templateUrl === this.loadedTemplateUrl) return;
            this.loadedTemplateUrl = templateUrl;
            this.setState({projectLoadStatus: 'loading', projectLoadProgress: 0});
            try {
                const response = await fetch(templateUrl, {credentials: 'include', cache: 'no-store'});
                if (!response.ok) throw new Error(`HTTP ${response.status} ${response.statusText}`);
                const expectedSize = Number(response.headers.get('content-length') || 0);
                let project;
                if (response.body?.getReader && expectedSize > 0) {
                    const reader = response.body.getReader();
                    const bytes = new Uint8Array(expectedSize);
                    let received = 0;
                    let lastProgress = 0;
                    while (true) { // eslint-disable-line no-constant-condition
                        const {done, value} = await reader.read(); // eslint-disable-line no-await-in-loop
                        if (done) break;
                        if (received + value.byteLength > expectedSize) {
                            throw new Error('服务器返回的文件大小超过预期');
                        }
                        bytes.set(value, received);
                        received += value.byteLength;
                        const progress = Math.min(100, Math.floor((received / expectedSize) * 100));
                        if (progress !== lastProgress) {
                            lastProgress = progress;
                            this.setState({projectLoadProgress: progress});
                        }
                    }
                    project = bytes.buffer.slice(0, received);
                } else {
                    project = await response.arrayBuffer();
                }
                if (expectedSize > 0 && project.byteLength !== expectedSize) {
                    throw new Error(`文件下载不完整（应为 ${expectedSize} 字节，实际 ${project.byteLength} 字节）`);
                }
                if (!project.byteLength) throw new Error('服务器返回了空文件');
                this.setState({projectLoadProgress: 100});
                await this.props.vm.loadProject(project);
                this.setState({projectLoadStatus: 'idle', projectLoadProgress: 100});
            } catch (error) {
                this.loadedTemplateUrl = '';
                this.setState({projectLoadStatus: 'idle', projectLoadProgress: 0});
                throw error;
            }
        }
        async exportProject (source, origin, requestId, action) {
            const blob = await normalizeProjectBlob(await this.props.vm.saveProjectSb3());
            const arrayBuffer = await blobToArrayBuffer(blob);
            const cover = await this.captureCover();
            source.postMessage({
                type: 'hydro:project',
                requestId,
                action,
                arrayBuffer,
                cover,
                title: this.props.reduxProjectTitle || query.get('projectTitle') || '未命名作品'
            }, origin || '*', [arrayBuffer]);
        }
        captureCover () {
            return new Promise(resolve => {
                const renderer = this.props.vm.renderer;
                if (!renderer || !renderer.requestSnapshot) {
                    resolve('');
                    return;
                }
                let finished = false;
                const finish = cover => {
                    if (finished) return;
                    finished = true;
                    resolve(cover || '');
                };
                renderer.requestSnapshot(finish);
                renderer.draw();
                window.setTimeout(() => finish(''), 2000);
            });
        }
        handleMessage (event) {
            const data = event.data || {};
            if (data.type === 'hydro:save-draft') {
                this.saveDraftToUrl(query.get('draftUrl'))
                    .then(() => {
                        event.source?.postMessage({
                            type: 'hydro:draft-saved', requestId: data.requestId
                        }, event.origin || '*');
                    })
                    .catch(error => {
                        event.source?.postMessage({
                            type: 'hydro:error', requestId: data.requestId, message: error.message
                        }, event.origin || '*');
                    });
            }
            if (data.type === 'hydro:init') {
                this.loadTemplate(data.templateUrl).catch(error => {
                    event.source?.postMessage({
                        type: 'hydro:error',
                        requestId: data.requestId,
                        message: error.message
                    }, event.origin || '*');
                });
            }
            if (data.type === 'hydro:load-project') {
                Promise.resolve(this.props.vm.loadProject(data.arrayBuffer))
                    .then(() => {
                        this.loadedTemplateUrl = '';
                        event.source?.postMessage({
                            type: 'hydro:project-loaded',
                            requestId: data.requestId
                        }, event.origin || '*');
                    })
                    .catch(error => {
                        event.source?.postMessage({
                            type: 'hydro:error',
                            requestId: data.requestId,
                            message: error.message
                        }, event.origin || '*');
                    });
            }
            if (data.type === 'hydro:export-project') {
                this.exportProject(event.source, event.origin, data.requestId, data.action).catch(error => {
                    event.source?.postMessage({
                        type: 'hydro:error',
                        requestId: data.requestId,
                        message: error.message
                    }, event.origin || '*');
                });
            }
            if (data.type === 'hydro:save-result') {
                if (this.saveTimer) window.clearTimeout(this.saveTimer);
                this.saveTimer = null;
                if (data.ok && data.id) this.cloudProjectId = data.id;
                this.setState({saveStatus: data.ok ? 'success' : 'error'});
                if (data.ok && window.__HYDRO_RETURN_AFTER_SAVE__) {
                    window.__HYDRO_RETURN_AFTER_SAVE__ = false;
                    window.location.assign(query.get('returnUrl') || '/my-projects');
                }
            }
        }
        render () {
            const {
                judgeResult, operation, projectLoadProgress, projectLoadStatus,
                saveStatus, statusMessage, showProblemDetails, showRecords
            } = this.state;
            const examMode = query.get('examMode') === '1';
            return (
                <React.Fragment>
                    <WrappedComponent {...this.props} />
                    {enabled && projectLoadStatus === 'loading' && (
                        <div
                            style={{
                                alignItems: 'center',
                                background: '#f6f8fc',
                                bottom: 0,
                                display: 'flex',
                                justifyContent: 'center',
                                left: 0,
                                position: 'fixed',
                                right: 0,
                                top: 0,
                                zIndex: 100001
                            }}
                        >
                            <div style={{color: '#334155', maxWidth: '420px', padding: '32px', textAlign: 'center'}}>
                                <svg
                                    height="54"
                                    viewBox="0 0 40 40"
                                    width="54"
                                >
                                    <circle
                                        cx="20"
                                        cy="20"
                                        fill="none"
                                        r="15"
                                        stroke="#dbeafe"
                                        strokeWidth="4"
                                    />
                                    <path
                                        d="M20 5a15 15 0 0 1 15 15"
                                        fill="none"
                                        stroke="#2563eb"
                                        strokeLinecap="round"
                                        strokeWidth="4"
                                    >
                                        <animateTransform
                                            attributeName="transform"
                                            dur=".8s"
                                            from="0 20 20"
                                            repeatCount="indefinite"
                                            to="360 20 20"
                                            type="rotate"
                                        />
                                    </path>
                                </svg>
                                <h2 style={{fontSize: '22px', margin: '18px 0 8px'}}>{'正在加载 Scratch 作品'}</h2>
                                <p style={{color: '#64748b', lineHeight: 1.7, margin: 0}}>
                                    {projectLoadProgress < 100 ?
                                        `正在下载作品文件 ${projectLoadProgress}%` :
                                        '文件下载完成，正在解压并载入素材…'}
                                </p>
                                <div
                                    style={{
                                        background: '#dbeafe',
                                        borderRadius: '999px',
                                        height: '8px',
                                        marginTop: '20px',
                                        overflow: 'hidden'
                                    }}
                                >
                                    <div
                                        style={{
                                            background: '#2563eb',
                                            borderRadius: 'inherit',
                                            height: '100%',
                                            transition: 'width .15s ease',
                                            width: `${projectLoadProgress}%`
                                        }}
                                    />
                                </div>
                                <small style={{color: '#94a3b8', display: 'block', marginTop: '14px'}}>
                                    {'大型作品需要更长时间，请不要关闭页面'}
                                </small>
                            </div>
                        </div>
                    )}
                    {enabled && showRecords && recordUrls && <HydroRecords
                        urls={recordUrls}
                        onClose={this.handleHideRecords}
                    />}
                    {enabled && showProblemDetails && (
                        <div
                            style={{
                                background: 'rgba(15, 23, 42, 0.58)',
                                bottom: 0,
                                left: 0,
                                padding: '32px',
                                position: 'fixed',
                                right: 0,
                                top: 0,
                                zIndex: 99999
                            }}
                            onClick={this.handleHideProblemDetails}
                        >
                            <div
                                style={{
                                    background: '#fff',
                                    borderRadius: '16px',
                                    boxShadow: '0 24px 70px rgba(15, 23, 42, .3)',
                                    height: 'calc(100vh - 64px)',
                                    margin: '0 auto',
                                    maxWidth: '920px',
                                    overflow: 'hidden',
                                    position: 'relative'
                                }}
                                onClick={this.handleProblemDetailsClick}
                            >
                                <button
                                    style={{
                                        background: '#eef2ff',
                                        border: 0,
                                        borderRadius: '50%',
                                        cursor: 'pointer',
                                        fontSize: '24px',
                                        height: '38px',
                                        position: 'absolute',
                                        right: '16px',
                                        top: '14px',
                                        width: '38px',
                                        zIndex: 2
                                    }}
                                    onClick={this.handleHideProblemDetails}
                                >{'\u00d7'}</button>
                                {query.get('examMode') === '1' ? (
                                    <div
                                        style={{height: '100%', overflow: 'auto', padding: '38px 42px'}}
                                        /* eslint-disable-next-line react/no-danger */
                                        dangerouslySetInnerHTML={{
                                            __html: query.get('problemHtml') || '<h2>暂无题目详情</h2>'
                                        }}
                                    />
                                ) : (
                                    <iframe
                                        src={query.get('detailsUrl') || query.get('returnUrl')}
                                        style={{border: 0, height: '100%', width: '100%'}}
                                        title="题目详情"
                                    />
                                )}
                            </div>
                        </div>
                    )}
                    {enabled && judgeResult && (
                        <div
                            style={{
                                alignItems: examMode ? 'flex-start' : 'center',
                                background: 'rgba(15, 23, 42, 0.62)',
                                bottom: 0,
                                display: 'flex',
                                justifyContent: 'center',
                                left: 0,
                                position: 'fixed',
                                right: 0,
                                paddingTop: examMode ? '9vh' : 0,
                                top: 0,
                                zIndex: 100001
                            }}
                        >
                            {!examMode && judgeResult.status === 1 && (
                                <React.Fragment>
                                    <style>{`
                                        @keyframes hydro-confetti-flight {
                                            0% { opacity: 0; transform: translate(0, 0) rotate(0deg) scale(.5); }
                                            12% { opacity: 1; }
                                            100% {
                                                opacity: 0;
                                                transform: translate(var(--x), var(--y)) rotate(760deg) scale(1);
                                            }
                                        }
                                        @keyframes hydro-cannon-pop {
                                            0%, 100% { transform: rotate(var(--angle)) scale(1); }
                                            16% { transform: rotate(var(--angle)) scale(1.28); }
                                            32% { transform: rotate(var(--angle)) scale(.94); }
                                        }
                                        .hydro-celebration-cannon {
                                            bottom: 7vh;
                                            font-size: clamp(54px, 7vw, 100px);
                                            position: fixed;
                                            z-index: 100003;
                                            animation: hydro-cannon-pop .9s ease-out 3;
                                        }
                                        .hydro-celebration-cannon.left { --angle: -25deg; left: 3vw; }
                                        .hydro-celebration-cannon.right { --angle: 25deg; right: 3vw; }
                                        .hydro-confetti-piece {
                                            animation: hydro-confetti-flight 2.7s cubic-bezier(.12,.6,.28,1) forwards;
                                            animation-delay: var(--delay);
                                            background: var(--color);
                                            border-radius: 2px;
                                            bottom: 14vh;
                                            height: 14px;
                                            position: fixed;
                                            width: 8px;
                                            z-index: 100002;
                                        }
                                        .hydro-confetti-piece.left { left: 8vw; }
                                        .hydro-confetti-piece.right { right: 8vw; }
                                    `}</style>
                                    <div className="hydro-celebration-cannon left">{'🎉'}</div>
                                    <div className="hydro-celebration-cannon right">{'🎉'}</div>
                                    {celebrationPieces.map(index => {
                                        const side = index % 2 ? 'right' : 'left';
                                        const direction = side === 'left' ? 1 : -1;
                                        const distance = 120 + ((index * 47) % 360);
                                        const rise = -180 - ((index * 71) % 520);
                                        const colors = [
                                            '#ff4d6d', '#ffd43b', '#4dabf7', '#69db7c', '#b197fc', '#ff922b'
                                        ];
                                        return (
                                            <i
                                                className={`hydro-confetti-piece ${side}`}
                                                key={index}
                                                style={{
                                                    '--color': colors[index % colors.length],
                                                    '--delay': `${(index % 10) * 0.06}s`,
                                                    '--x': `${direction * distance}px`,
                                                    '--y': `${rise}px`
                                                }}
                                            />
                                        );
                                    })}
                                </React.Fragment>
                            )}
                            <div
                                style={{
                                    background: '#fff',
                                    borderRadius: '18px',
                                    maxHeight: 'calc(100vh - 48px)',
                                    maxWidth: examMode ? '440px' : '1040px',
                                    overflow: 'auto',
                                    padding: 0,
                                    position: 'relative',
                                    width: 'calc(100% - 48px)'
                                }}
                            >
                                <div
                                    style={{
                                        alignItems: 'center',
                                        background: '#fff',
                                        display: 'flex',
                                        justifyContent: examMode ? 'flex-end' : 'space-between',
                                        padding: examMode ? '14px 16px 0' : '18px 18px 12px 36px',
                                        position: 'sticky',
                                        top: 0,
                                        zIndex: 2
                                    }}
                                >
                                    {!examMode && (
                                        <div style={{color: '#64748b', fontSize: '14px', fontWeight: 700}}>
                                            {'作品评测结果'}
                                        </div>
                                    )}
                                    <button
                                        aria-label="关闭评测结果"
                                        style={{
                                            alignItems: 'center',
                                            background: '#f1f5f9',
                                            border: '1px solid #cbd5e1',
                                            borderRadius: '10px',
                                            color: '#334155',
                                            cursor: 'pointer',
                                            display: 'flex',
                                            fontSize: '26px',
                                            height: '40px',
                                            justifyContent: 'center',
                                            lineHeight: 1,
                                            padding: 0,
                                            width: '40px'
                                        }}
                                        title="关闭"
                                        type="button"
                                        onClick={this.handleCloseJudgeResult}
                                    >{'×'}</button>
                                </div>
                                <div style={{padding: examMode ? '8px 28px 28px' : '0 36px 30px'}}>
                                    {examMode ? (
                                        <div
                                            style={{
                                                alignItems: 'center',
                                                background: (judgeResult.passedCases ?? judgeResult.passed ?? 0) ===
                                                    (judgeResult.totalCases ?? judgeResult.total ?? 0) &&
                                                    (judgeResult.totalCases ?? judgeResult.total ?? 0) > 0 ?
                                                    'linear-gradient(135deg, #ecfdf5, #f0fdf4)' :
                                                    'linear-gradient(135deg, #fff7ed, #fffbeb)',
                                                border: `2px solid ${((judgeResult.passedCases ??
                                                    judgeResult.passed ?? 0) === (judgeResult.totalCases ??
                                                    judgeResult.total ?? 0) && (judgeResult.totalCases ??
                                                    judgeResult.total ?? 0) > 0) ? '#86efac' : '#fed7aa'}`,
                                                borderRadius: '18px',
                                                color: '#334155',
                                                display: 'flex',
                                                flexDirection: 'column',
                                                justifyContent: 'center',
                                                padding: '18px 16px 20px',
                                                textAlign: 'center'
                                            }}
                                        >
                                            <div style={{fontSize: '38px', lineHeight: 1}}>
                                                {(judgeResult.passedCases ?? judgeResult.passed ?? 0) ===
                                                (judgeResult.totalCases ?? judgeResult.total ?? 0) &&
                                                (judgeResult.totalCases ?? judgeResult.total ?? 0) > 0 ? '✅' : '📋'}
                                            </div>
                                            <strong style={{fontSize: '20px', marginTop: '9px'}}>
                                                {'判题结果'}
                                            </strong>
                                            <div
                                                style={{
                                                    display: 'grid',
                                                    gap: '8px',
                                                    marginTop: '14px',
                                                    width: '100%'
                                                }}
                                            >
                                                <div
                                                    style={{
                                                        alignItems: 'center',
                                                        background: '#fff',
                                                        border: '1px solid #e2e8f0',
                                                        borderRadius: '11px',
                                                        color: '#475569',
                                                        display: 'flex',
                                                        fontSize: '15px',
                                                        justifyContent: 'space-between',
                                                        padding: '10px 14px'
                                                    }}
                                                >
                                                    <span>{'测试点总数'}</span>
                                                    <strong style={{color: '#334155', fontSize: '20px'}}>
                                                        {`共 ${judgeResult.totalCases ?? judgeResult.total ?? 0} 个`}
                                                    </strong>
                                                </div>
                                                <div
                                                    style={{
                                                        alignItems: 'center',
                                                        background: '#f5f3ff',
                                                        border: '1px solid #ddd6fe',
                                                        borderRadius: '11px',
                                                        color: '#6d28d9',
                                                        display: 'flex',
                                                        fontSize: '15px',
                                                        justifyContent: 'space-between',
                                                        padding: '10px 14px'
                                                    }}
                                                >
                                                    <span>{'通过测试点'}</span>
                                                    <strong style={{fontSize: '20px'}}>
                                                        {`通过 ${judgeResult.passedCases ?? judgeResult.passed ?? 0} 个`}
                                                    </strong>
                                                </div>
                                            </div>
                                        </div>
                                    ) : <RecordDetail result={judgeResult} />}
                                    <button
                                        style={{
                                            background: '#2563eb',
                                            border: 0,
                                            borderRadius: '10px',
                                            color: '#fff',
                                            cursor: 'pointer',
                                            display: examMode ? 'block' : 'inline-block',
                                            float: examMode ? 'none' : 'right',
                                            fontSize: '15px',
                                            marginTop: '22px',
                                            padding: '11px 28px',
                                            width: examMode ? '100%' : 'auto'
                                        }}
                                        onClick={this.handleCloseJudgeResult}
                                    >{'继续答题'}</button>
                                </div>
                            </div>
                        </div>
                    )}
                    {enabled && saveStatus !== 'idle' && (
                        <div
                            style={{
                                alignItems: examMode ? 'flex-start' : 'center',
                                background: 'rgba(15, 23, 42, 0.62)',
                                bottom: 0,
                                display: 'flex',
                                justifyContent: 'center',
                                left: 0,
                                position: 'fixed',
                                right: 0,
                                paddingTop: examMode ? '9vh' : 0,
                                top: 0,
                                zIndex: 100000
                            }}
                        >
                            <div
                                style={{
                                    background: 'linear-gradient(180deg, #ffffff 0%, #f8faff 100%)',
                                    border: '1px solid rgba(99, 102, 241, .18)',
                                    borderRadius: '24px',
                                    boxShadow: '0 28px 80px rgba(30, 41, 90, .32)',
                                    maxWidth: examMode ? '460px' : '480px',
                                    overflow: 'hidden',
                                    padding: examMode ? '0 34px 28px' : '0 40px 34px',
                                    position: 'relative',
                                    textAlign: 'center',
                                    width: 'calc(100% - 40px)'
                                }}
                            >
                                <div
                                    style={{
                                        background: saveStatus === 'error' ?
                                            'linear-gradient(90deg, #fb7185, #ef4444)' :
                                            (saveStatus === 'success' ?
                                                'linear-gradient(90deg, #34d399, #22c55e)' :
                                                'linear-gradient(90deg, #6366f1, #8b5cf6, #3b82f6)'),
                                        height: '6px',
                                        margin: '0 -40px 24px'
                                    }}
                                />
                                <div
                                    style={{
                                        alignItems: 'center',
                                        background: saveStatus === 'error' ? '#fff1f2' :
                                            (saveStatus === 'success' ? '#ecfdf5' : '#eef2ff'),
                                        border: `1px solid ${saveStatus === 'error' ? '#fecdd3' :
                                            (saveStatus === 'success' ? '#a7f3d0' : '#c7d2fe')}`,
                                        borderRadius: '50%',
                                        color: saveStatus === 'error' ? '#dc2626' :
                                            (saveStatus === 'success' ? '#16a34a' : '#6366f1'),
                                        display: 'flex',
                                        fontSize: examMode ? '25px' : '30px',
                                        fontWeight: 900,
                                        height: examMode ? '60px' : '68px',
                                        justifyContent: 'center',
                                        margin: examMode ? '0 auto 16px' : '0 auto 18px',
                                        width: examMode ? '60px' : '68px'
                                    }}
                                >
                                    {saveStatus === 'saving' ? (
                                        <svg
                                            height="34"
                                            viewBox="0 0 40 40"
                                            width="34"
                                        >
                                            <circle
                                                cx="20"
                                                cy="20"
                                                fill="none"
                                                r="15"
                                                stroke="#c7d2fe"
                                                strokeWidth="4"
                                            />
                                            <path
                                                d="M20 5a15 15 0 0 1 15 15"
                                                fill="none"
                                                stroke="#6366f1"
                                                strokeLinecap="round"
                                                strokeWidth="4"
                                            >
                                                <animateTransform
                                                    attributeName="transform"
                                                    dur=".8s"
                                                    from="0 20 20"
                                                    repeatCount="indefinite"
                                                    to="360 20 20"
                                                    type="rotate"
                                                />
                                            </path>
                                        </svg>
                                    ) : (saveStatus === 'success' ? '✓' : '!')}
                                </div>
                                <div style={{color: '#172033', fontSize: examMode ? '21px' : '23px', fontWeight: 900}}>
                                    {saveStatus === 'saving' && (operation === 'judge' ?
                                        '正在提交并评测，请稍等…' : '正在保存到云端，请稍等…')}
                                    {saveStatus === 'success' && '保存成功'}
                                    {saveStatus === 'error' && (operation === 'judge' ?
                                        '提交评测失败' : '保存失败')}
                                </div>
                                <div style={{color: '#64748b', fontSize: '14px', lineHeight: 1.6, marginTop: '8px'}}>
                                    {statusMessage || (saveStatus === 'saving' ?
                                        (operation === 'judge' ?
                                            '作品已上传，正在等待判题服务返回结果' :
                                            '正在导出并上传 Scratch 作品') :
                                        (saveStatus === 'success' ?
                                            '作品已保存到“我的作品”' :
                                            (operation === 'judge' ?
                                                '请关闭提示后重新提交答题' :
                                                '请关闭提示后再次选择“另存到云端”')))}
                                </div>
                                {saveStatus === 'saving' && operation === 'judge' && (
                                    <div
                                        style={{
                                            alignItems: 'center',
                                            background: '#fff',
                                            border: '1px solid #e2e8f0',
                                            borderRadius: '14px',
                                            display: 'grid',
                                            gap: '5px',
                                            gridTemplateColumns: '1fr 24px 1fr 24px 1fr',
                                            marginTop: '22px',
                                            padding: '13px 14px'
                                        }}
                                    >
                                        <span style={{color: '#16a34a', fontSize: '12px', fontWeight: 800}}>
                                            {'✓ 上传完成'}
                                        </span>
                                        <span style={{color: '#cbd5e1'}}>{'›'}</span>
                                        <span style={{color: '#6366f1', fontSize: '12px', fontWeight: 900}}>
                                            {'● 正在评测'}
                                        </span>
                                        <span style={{color: '#cbd5e1'}}>{'›'}</span>
                                        <span style={{color: '#94a3b8', fontSize: '12px', fontWeight: 700}}>
                                            {'查看结果'}
                                        </span>
                                    </div>
                                )}
                                {saveStatus !== 'saving' && (
                                    <button
                                        style={{
                                            background: saveStatus === 'success' ? '#16a34a' : '#2563eb',
                                            border: 0,
                                            borderRadius: '10px',
                                            color: '#fff',
                                            cursor: 'pointer',
                                            fontSize: '15px',
                                            fontWeight: 900,
                                            marginTop: '22px',
                                            padding: '11px 28px'
                                        }}
                                        onClick={this.handleCloseSaveStatus}
                                    >
                                        {saveStatus === 'success' ? '完成' : '关闭提示'}
                                    </button>
                                )}
                            </div>
                        </div>
                    )}
                </React.Fragment>
            );
        }
    }

    HydroBridge.propTypes = {
        reduxProjectTitle: PropTypes.string,
        vm: PropTypes.instanceOf(VM).isRequired
    };

    return connect(state => ({
        reduxProjectTitle: state.scratchGui.projectTitle
    }))(HydroBridge);
};

export default HydroBridgeHOC;
